import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from "remotion";
import { Lock, Zap, Star, Check, X, ArrowUpRight } from "lucide-react";

export const KyuStoreFCMobileBrutalist: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // ==========================================
  // TIMING & SPRINGS (18.72s = 565 frames @ 30fps)
  // Perfectly mapped to exact mastered energetic audio
  // ==========================================
  const marqueeOffset = interpolate(frame, [0, 565], [0, -1100]);

  // Scene 1: Frame 0 - 178 (Hook & Warning: 0s - 5.95s)
  const heroSpring = spring({
    frame,
    fps,
    config: { damping: 12, stiffness: 180 },
  });

  const titleMask = interpolate(frame, [5, 24], [100, 0], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Scene 2: Frame 178 - 468 (Price Breakdown & Trust: 5.95s - 15.6s)
  const s2Frame = frame - 178;
  const card1Spring = spring({
    frame: s2Frame - 5,
    fps,
    config: { damping: 14, stiffness: 160 },
  });
  const card2Spring = spring({
    frame: s2Frame - 18,
    fps,
    config: { damping: 14, stiffness: 160 },
  });
  const card3Spring = spring({
    frame: s2Frame - 32,
    fps,
    config: { damping: 14, stiffness: 160 },
  });

  // Scene 3: Frame 468 - 565 (CTA Satset: 15.6s - 18.7s)
  const s3Frame = frame - 468;
  const ctaScale = spring({
    frame: s3Frame,
    fps,
    config: { damping: 12, stiffness: 150 },
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#070709",
        color: "#FFFFFF",
        fontFamily: "'Anton', 'Bebas Neue', 'Impact', sans-serif",
        overflow: "hidden",
      }}
    >
      {/* Exact Synchronized Mastered Broadcast Audio Track */}
      <Audio src={staticFile("vo_fcmobile_exact_mastered.mp3")} volume={1.0} />

      {/* Background Architectural Grid */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(204,255,0,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(204,255,0,0.06) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          opacity: 0.85,
        }}
      />

      {/* Top Safe-Zone Ticker Tape (Y = 250px) */}
      <div
        style={{
          position: "absolute",
          top: 250,
          left: -80,
          right: -80,
          height: 48,
          backgroundColor: "#CCFF00",
          color: "#000000",
          display: "flex",
          alignItems: "center",
          transform: "rotate(-1.5deg)",
          overflow: "hidden",
          boxShadow: "0 8px 25px rgba(204,255,0,0.4)",
          zIndex: 60,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 32,
            whiteSpace: "nowrap",
            fontSize: 22,
            fontWeight: 900,
            letterSpacing: "0.15em",
            transform: `translateX(${marqueeOffset}px)`,
          }}
        >
          <span>/// JANGAN BELI DI GAME KEMAHALAN /// KYU STORE JAUH LEBIH HEMAT /// PROSES DALAM SEDETIK /// TANPA PASSWORD ///</span>
          <span>/// JANGAN BELI DI GAME KEMAHALAN /// KYU STORE JAUH LEBIH HEMAT /// PROSES DALAM SEDETIK /// TANPA PASSWORD ///</span>
          <span>/// JANGAN BELI DI GAME KEMAHALAN /// KYU STORE JAUH LEBIH HEMAT /// PROSES DALAM SEDETIK /// TANPA PASSWORD ///</span>
        </div>
      </div>

      {/* ==============================================================
          SCENE 1: THE CASUAL HOOK (Frame 0 - 178 / 0s - 5.95s)
          VO: "Mau beli koin FC Mobile harga murah dan resmi? Stop beli di game langsung, kemahalan!"
          ============================================================== */}
      {frame < 178 && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: 50,
            right: 50,
            transform: "translateY(-50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 22,
            zIndex: 30,
          }}
        >
          {/* Athlete Visual Container */}
          <div
            style={{
              width: "100%",
              height: 520,
              borderRadius: 24,
              overflow: "hidden",
              border: "3px solid #CCFF00",
              boxShadow: "10px 10px 0px #CCFF00",
              backgroundColor: "#111216",
              position: "relative",
              transform: `scale(${0.92 + heroSpring * 0.08})`,
            }}
          >
            <Img
              src={staticFile("assets_library/dynamic_renders/fcmobile_cover_art.png")}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center 30%",
                filter: "contrast(1.15) saturate(1.1)",
              }}
            />
            {/* Warning Stop Badge */}
            <div
              style={{
                position: "absolute",
                top: 20,
                right: 20,
                backgroundColor: "#EF4444",
                color: "#FFFFFF",
                padding: "8px 18px",
                fontSize: 20,
                fontWeight: 900,
                border: "2px solid #FFFFFF",
                transform: "rotate(4deg)",
                boxShadow: "4px 4px 0px #000000",
              }}
            >
              KEMAHALAN!
            </div>

            <div
              style={{
                position: "absolute",
                bottom: 16,
                left: 16,
                backgroundColor: "rgba(0,0,0,0.85)",
                border: "2px solid #CCFF00",
                padding: "6px 14px",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#CCFF00" }} />
              <span style={{ fontSize: 16, color: "#FFFFFF", letterSpacing: "0.1em" }}>
                EA SPORTS FC MOBILE
              </span>
            </div>
          </div>

          {/* Simple Direct Headline */}
          <div style={{ width: "100%", textAlign: "left" }}>
            <div style={{ overflow: "hidden" }}>
              <div
                style={{
                  fontSize: 66,
                  lineHeight: 1.15,
                  fontWeight: 900,
                  textTransform: "uppercase",
                  color: "#FFFFFF",
                  letterSpacing: "0.04em",
                  transform: `translateY(${titleMask}%)`,
                }}
              >
                MAU BELI KOIN <span style={{ color: "#CCFF00" }}>FC MOBILE?</span>
              </div>
            </div>
            <div style={{ overflow: "hidden", marginTop: 4 }}>
              <div
                style={{
                  fontSize: 54,
                  lineHeight: 1.15,
                  fontWeight: 900,
                  textTransform: "uppercase",
                  color: "#EF4444",
                  letterSpacing: "0.04em",
                  transform: `translateY(${titleMask}%)`,
                }}
              >
                JANGAN BELI DI GAME LANGSUNG!
              </div>
            </div>
          </div>

          {/* Simple Comparison Grid (Spacious, High Contrast, Large Text) */}
          <div
            style={{
              width: "100%",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
            }}
          >
            {/* Card 1: Di Game Langsung */}
            <div
              style={{
                backgroundColor: "#18181B",
                border: "3px solid #EF4444",
                boxShadow: "6px 6px 0px #EF4444",
                padding: "18px 20px",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <div style={{ fontSize: 18, color: "#A1A1AA", fontWeight: 800, letterSpacing: "0.08em" }}>
                DI GAME RESMI
              </div>
              <div
                style={{
                  fontSize: 30,
                  color: "#EF4444",
                  fontWeight: 900,
                  letterSpacing: "0.05em",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>KEMAHALAN!</span>
                <X size={26} strokeWidth={3.5} />
              </div>
            </div>

            {/* Card 2: Kyu Store */}
            <div
              style={{
                backgroundColor: "#18181B",
                border: "3px solid #CCFF00",
                boxShadow: "6px 6px 0px #CCFF00",
                padding: "18px 20px",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <div style={{ fontSize: 18, color: "#CCFF00", fontWeight: 800, letterSpacing: "0.08em" }}>
                KYU STORE
              </div>
              <div
                style={{
                  fontSize: 30,
                  color: "#FFFFFF",
                  fontWeight: 900,
                  letterSpacing: "0.05em",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>JAUH HEMAT!</span>
                <Check size={26} color="#CCFF00" strokeWidth={3.5} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==============================================================
          SCENE 2: PRICE TEARDOWN (Frame 178 - 468 / 5.95s - 15.6s)
          VO: "Di Kyu Store, harganya jauh lebih murah dan seratus persen resmi.
               Cukup masukin ID game kamu, bayar pakai QRIS, langsung masuk dalam hitungan detik tanpa password!"
          ============================================================== */}
      {frame >= 178 && frame < 468 && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: 50,
            right: 50,
            transform: "translateY(-50%)",
            display: "flex",
            flexDirection: "column",
            gap: 20,
            zIndex: 40,
          }}
        >
          {/* Header */}
          <div>
            <div
              style={{
                display: "inline-block",
                backgroundColor: "#CCFF00",
                color: "#000000",
                padding: "8px 20px",
                fontSize: 20,
                fontWeight: 900,
                letterSpacing: "0.12em",
                marginBottom: 8,
              }}
            >
              HARGA RESMI SERVER
            </div>
            <div style={{ fontSize: 50, fontWeight: 900, color: "#FFFFFF", lineHeight: 1.1, letterSpacing: "0.04em" }}>
              PILIH KOIN SQUAD KAMU:
            </div>
          </div>

          {/* Tier 1: 1.000 Silver */}
          <div
            style={{
              backgroundColor: "#18181B",
              border: "3px solid #FFFFFF",
              boxShadow: "6px 6px 0px #FFFFFF",
              padding: "20px 26px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              transform: `scale(${Math.max(0, card1Spring)})`,
            }}
          >
            <div>
              <div style={{ fontSize: 20, color: "#A1A1AA", fontWeight: 800, letterSpacing: "0.06em", marginBottom: 4 }}>
                PAKET HEMAT - 1.000 SILVER
              </div>
              <div style={{ fontSize: 48, fontWeight: 900, color: "#FFFFFF", lineHeight: 1, letterSpacing: "0.02em" }}>
                RP 15.000
              </div>
            </div>
            <div
              style={{
                backgroundColor: "#FFFFFF",
                color: "#000000",
                padding: "10px 22px",
                fontSize: 22,
                fontWeight: 900,
                letterSpacing: "0.05em",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span>PILIH</span>
              <ArrowUpRight size={22} strokeWidth={3} />
            </div>
          </div>

          {/* Tier 2: 1.050 FC Points (Highlighted Hero Card with Vector Star) */}
          <div
            style={{
              backgroundColor: "#CCFF00",
              border: "4px solid #FFFFFF",
              boxShadow: "8px 8px 0px #FFFFFF",
              padding: "24px 26px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              transform: `scale(${Math.max(0, card2Spring)}) rotate(-1deg)`,
            }}
          >
            <div>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  backgroundColor: "#000000",
                  color: "#CCFF00",
                  padding: "6px 14px",
                  fontSize: 16,
                  fontWeight: 900,
                  letterSpacing: "0.08em",
                  marginBottom: 6,
                }}
              >
                <Star size={16} fill="#CCFF00" strokeWidth={0} />
                <span>HEMAT 45 RIBU</span>
              </div>
              <div style={{ fontSize: 24, color: "#000000", fontWeight: 900, letterSpacing: "0.05em", marginBottom: 2 }}>
                1.050 FC POINTS
              </div>
              <div style={{ fontSize: 54, fontWeight: 900, color: "#000000", lineHeight: 1, letterSpacing: "0.02em" }}>
                RP 145.000
              </div>
            </div>
            <div
              style={{
                backgroundColor: "#000000",
                color: "#CCFF00",
                padding: "12px 24px",
                fontSize: 24,
                fontWeight: 900,
                letterSpacing: "0.05em",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span>GAS ORDER</span>
              <ArrowUpRight size={24} strokeWidth={3} />
            </div>
          </div>

          {/* Tier 3: Star Pass Premium */}
          <div
            style={{
              backgroundColor: "#18181B",
              border: "3px solid #CCFF00",
              boxShadow: "6px 6px 0px #CCFF00",
              padding: "20px 26px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              transform: `scale(${Math.max(0, card3Spring)})`,
            }}
          >
            <div>
              <div style={{ fontSize: 20, color: "#CCFF00", fontWeight: 800, letterSpacing: "0.06em", marginBottom: 4 }}>
                FULL UNLOCK - STAR PASS PREMIUM
              </div>
              <div style={{ fontSize: 48, fontWeight: 900, color: "#FFFFFF", lineHeight: 1, letterSpacing: "0.02em" }}>
                RP 149.000
              </div>
            </div>
            <div
              style={{
                backgroundColor: "#CCFF00",
                color: "#000000",
                padding: "10px 22px",
                fontSize: 22,
                fontWeight: 900,
                letterSpacing: "0.05em",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span>PILIH</span>
              <ArrowUpRight size={22} strokeWidth={3} />
            </div>
          </div>

          {/* Trust Sub-Bar (Zero Raw Emojis, Pure Lucide Vector Icons) */}
          <div
            style={{
              backgroundColor: "#000000",
              border: "2px solid #3F3F46",
              padding: "16px 22px",
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",
              fontSize: 19,
              letterSpacing: "0.05em",
              fontWeight: 800,
              color: "#E4E4E7",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Lock size={20} color="#CCFF00" />
              <span>100% AMAN TANPA PASSWORD</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Zap size={20} color="#CCFF00" fill="#CCFF00" />
              <span>SCAN QRIS LANGSUNG MASUK</span>
            </div>
          </div>
        </div>
      )}

      {/* ==============================================================
          SCENE 3: FINAL CALL TO ACTION (Frame 468 - 565 / 15.6s - 18.7s)
          VO: "Yuk, langsung ke KyuStore.com sekarang!"
          ============================================================== */}
      {frame >= 468 && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: 50,
            right: 50,
            transform: "translateY(-50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            zIndex: 40,
            transformOrigin: "center center",
            scale: `${Math.max(0, ctaScale)}`,
          }}
        >
          {/* Badge */}
          <div
            style={{
              backgroundColor: "#CCFF00",
              color: "#000000",
              fontSize: 24,
              fontWeight: 900,
              padding: "10px 28px",
              letterSpacing: "0.14em",
              border: "3px solid #FFFFFF",
              boxShadow: "6px 6px 0px #FFFFFF",
              marginBottom: 28,
            }}
          >
            PROSES DETIKAN OTOMATIS
          </div>

          <div
            style={{
              fontSize: 70,
              fontWeight: 900,
              lineHeight: 1.15,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              marginBottom: 22,
            }}
          >
            CUKUP MASUKIN
            <br />
            <span style={{ color: "#CCFF00" }}>ID GAME KAMU!</span>
          </div>

          <p
            style={{
              fontSize: 26,
              lineHeight: 1.35,
              letterSpacing: "0.03em",
              fontFamily: "sans-serif",
              color: "#E4E4E7",
              margin: "0 0 36px 0",
              maxWidth: 760,
            }}
          >
            Bayar pakai QRIS, koin langsung masuk dalam hitungan detik tanpa nunggu admin.
          </p>

          {/* Website Pill (Zero Raw Emojis, Pure Lucide Vector Icon) */}
          <div
            style={{
              backgroundColor: "#FFFFFF",
              color: "#000000",
              fontSize: 48,
              fontWeight: 900,
              letterSpacing: "0.06em",
              padding: "22px 48px",
              borderRadius: 16,
              border: "4px solid #CCFF00",
              boxShadow: "10px 10px 0px #CCFF00",
              display: "flex",
              alignItems: "center",
              gap: 16,
            }}
          >
            <span>WWW.KYUSTORE.COM</span>
            <ArrowUpRight size={44} strokeWidth={3.5} />
          </div>
        </div>
      )}

      {/* ==============================================================
          DYNAMIC SUBTITLE (CLEAN TEXT TYPOGRAPHY - ZERO WORD BACKGROUNDS)
          100% Word-for-Word Synchronized with Voiceover Audio
          Safe Zone: Y = 1470px
          ============================================================== */}
      {(() => {
        interface SubtitleWord {
          text: string;
          color?: string;
        }

        interface SubtitleCueItem {
          from: number;
          to: number;
          words: SubtitleWord[];
        }

        const EXACT_SUBTITLE_CUES: SubtitleCueItem[] = [
          {
            from: 3,
            to: 92,
            words: [
              { text: "Mau beli koin" },
              { text: "FC MOBILE", color: "#CCFF00" },
              { text: "harga murah dan" },
              { text: "RESMI?", color: "#CCFF00" },
            ],
          },
          {
            from: 92,
            to: 178,
            words: [
              { text: "STOP", color: "#EF4444" },
              { text: "beli di game langsung," },
              { text: "KEMAHALAN!", color: "#EF4444" },
            ],
          },
          {
            from: 178,
            to: 300,
            words: [
              { text: "Di" },
              { text: "KYU STORE,", color: "#CCFF00" },
              { text: "harganya" },
              { text: "JAUH LEBIH MURAH", color: "#CCFF00" },
              { text: "dan seratus persen resmi." },
            ],
          },
          {
            from: 300,
            to: 468,
            words: [
              { text: "Cukup masukin" },
              { text: "ID GAME KAMU,", color: "#CCFF00" },
              { text: "bayar pakai" },
              { text: "QRIS,", color: "#CCFF00" },
              { text: "langsung masuk dalam hitungan detik" },
              { text: "TANPA PASSWORD!", color: "#CCFF00" },
            ],
          },
          {
            from: 468,
            to: 565,
            words: [
              { text: "Yuk, langsung ke" },
              { text: "KYUSTORE.COM", color: "#CCFF00" },
              { text: "SEKARANG!", color: "#CCFF00" },
            ],
          },
        ];

        const active = EXACT_SUBTITLE_CUES.find((c) => frame >= c.from && frame < c.to);
        if (!active) return null;

        return (
          <div
            style={{
              position: "absolute",
              top: 1470,
              left: 50,
              right: 50,
              display: "flex",
              justifyContent: "center",
              zIndex: 70,
            }}
          >
            <div
              style={{
                backgroundColor: "rgba(0,0,0,0.88)",
                border: "2px solid #CCFF00",
                boxShadow: "6px 6px 0px rgba(0,0,0,0.9)",
                padding: "16px 28px",
                borderRadius: 20,
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                alignItems: "center",
                gap: "8px",
                maxWidth: 960,
                textAlign: "center",
              }}
            >
              {active.words.map((item, idx) => (
                <span
                  key={idx}
                  style={{
                    fontSize: 30,
                    lineHeight: 1.25,
                    fontWeight: 900,
                    letterSpacing: "0.03em",
                    fontFamily: "'Anton', sans-serif",
                    textTransform: "uppercase",
                    color: item.color || "#FFFFFF",
                    // STRICTLY NO INDIVIDUAL WORD BACKGROUND BOXES!
                    backgroundColor: "transparent",
                    padding: 0,
                  }}
                >
                  {item.text}
                </span>
              ))}
            </div>
          </div>
        );
      })()}
    </AbsoluteFill>
  );
};
